import React, { useState, useEffect, useMemo } from "react";
import MaterialCategory from "./MaterialCategory";
import MaterialComponent from "./MaterialComponent";
import { fetchMaterialCatalog } from "../../utils/MaterialCatalog";

/**
 * Generic material category component that works for any material type
 * @param {string} categoryName - The category name (e.g., "Concrete", "Steel", "Lumber")
 * @param {Array} dealers - List of dealers for this category
 */
export default function GenericMaterialCategory({ categoryName, dealers, clientId }) {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const catalog = await fetchMaterialCatalog();
        const categoryItems = catalog[categoryName] || [];
        setSubcategories(categoryItems);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    loadCatalog();
  }, [categoryName]);

  // Create a dynamic context that generates components on-the-fly
  const dynamicContext = useMemo(() => {
    if (!subcategories.length) return null;

    // Create a mock webpack context-like object
    const mockContext = {
      keys: () => subcategories.map(item => `./${item.name}.js`),
      resolve: (key) => key,
    };

    // Override the context call to return dynamic components
    const contextFn = (key) => {
      const subcatName = key.replace("./", "").replace(/\.(js|jsx)$/, "");
      const subcatData = subcategories.find(s => s.name === subcatName);
      
      if (!subcatData) return { default: () => null };

      return {
        default: (props) => (
          <MaterialComponent
            categoryName={categoryName}
            subcategoryName={subcatData.name}
            unit={subcatData.unit}
            unitCode={subcatData.unitCode}
            {...props}
            clientId={clientId}
          />
        ),
      };
    };

    // Merge the mock context properties with the function
    Object.assign(contextFn, mockContext);
    return contextFn;
  }, [subcategories, categoryName, clientId]);

  if (loading) {
    return <div className="text-gray-900 dark:text-gray-100 p-6">Loading {categoryName} materials...</div>;
  }

  if (error) {
    return <div className="text-red-600 dark:text-red-400 p-6">Error loading {categoryName} materials. Please try again.</div>;
  }

  if (!dynamicContext) {
    return <div className="text-gray-900 dark:text-gray-100 p-6">No {categoryName} materials available</div>;
  }

  return <MaterialCategory title={categoryName} context={dynamicContext} dealers={dealers} />;
}
